import { useCallback, useEffect } from "react";

import { toast } from "sonner";

import {
  CGM_WARMUP_DURATION_MS,
  isCgmConnectionJourneyStep,
} from "src/constants/cgm";
import { POST_MESSAGE_EVENTS, STORAGE_KEYS } from "src/constants/common";
import { sendMessageToParent } from "src/helpers/common";
import storage from "src/helpers/storage";
import { parseCgmMessage } from "src/services/cgm";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import {
  bindFailed,
  bindSuccess,
  clearScannedDevices,
  connected,
  cgmError,
  deviceFound,
  glucoseDataReceived,
  sensorStateChanged,
  setStep,
  statusUpdate,
  disconnected,
  warmupStarted,
} from "src/redux/slices/cgmSlice";
import type {
  CgmDeviceFoundPayload,
  PocDeviceJson,
  PocGlucoseJson,
} from "src/types/cgm.types";

/**
 * Resolves warmup end from SDK init/bind dates, storage, or fallback.
 * See ContinuousGlucoseMonitor / CGM status flow.
 */
export function resolveCgmWarmupEnd(
  initEndDate?: string | null,
  bindDate?: string | null
): string | null {
  if (initEndDate) {
    const ts = new Date(initEndDate).getTime();
    if (!Number.isNaN(ts) && ts > Date.now()) {
      return initEndDate;
    }
    if (!Number.isNaN(ts)) {
      console.log(
        "[CGM] resolveWarmupEnd — initEndDate in the past or invalid, trying bindDate / fallback"
      );
    }
  }

  if (bindDate) {
    const bindTs = new Date(bindDate).getTime();
    if (!Number.isNaN(bindTs)) {
      const computed = bindTs + CGM_WARMUP_DURATION_MS;
      if (computed > Date.now()) {
        return new Date(computed).toISOString();
      }
      console.log(
        "[CGM] resolveWarmupEnd — computed end is in the past, warmup done"
      );
      return null;
    }
  }

  const stored = storage.getItem(STORAGE_KEYS.CGM_WARMUP_END_DATE);
  if (stored) {
    const storedTs = new Date(stored).getTime();
    if (!Number.isNaN(storedTs) && storedTs > Date.now()) {
      return stored;
    }
    if (!Number.isNaN(storedTs)) {
      console.log(
        "[CGM] resolveWarmupEnd — stored warmup end in past, warmup done"
      );
      return null;
    }
  }

  console.log(
    "[CGM] resolveWarmupEnd — no valid dates, using now + 65 min fallback"
  );
  return new Date(Date.now() + CGM_WARMUP_DURATION_MS).toISOString();
}

/**
 * Subscribes to RN → WebView CGM postMessage events and updates Redux.
 * Used by both the connection journey and CGM status screens so state stays
 * consistent regardless of which route is mounted.
 */
export function useCgmWebViewMessages(): void {
  const dispatch = useAppDispatch();
  const cgm = useAppSelector((state) => state.cgm);

  const resolveWarmupEnd = useCallback(
    (initEndDate?: string | null, bindDate?: string | null) =>
      resolveCgmWarmupEnd(initEndDate, bindDate),
    []
  );

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const payload = parseCgmMessage(event);
      if (!payload) return;
      const { action } = payload;
      const data = payload.data as Record<string, unknown> | undefined;

      if (action?.startsWith("CGM_")) {
        console.log("[CGM] <<", action, data ?? "(no data)");
      }

      switch (action) {
        case POST_MESSAGE_EVENTS.CGM_DEVICE_FOUND:
          if (data) {
            dispatch(deviceFound(data as unknown as CgmDeviceFoundPayload));
          }
          break;

        case POST_MESSAGE_EVENTS.CGM_BIND_SUCCESS:
          if (data) {
            const d = data as {
              initEndDate?: string | null;
              bindDate?: string | null;
              pocDevice: CgmDeviceFoundPayload["pocDevice"];
            };
            let warmupEnd = resolveWarmupEnd(d.initEndDate, d.bindDate);
            if (
              !warmupEnd &&
              d.bindDate &&
              Date.now() - new Date(d.bindDate).getTime() < 2 * 60 * 1000
            ) {
              warmupEnd = new Date(
                Date.now() + CGM_WARMUP_DURATION_MS
              ).toISOString();
              console.log(
                "[CGM] bind success — initEndDate in past but bindDate recent, using warmup fallback"
              );
            }
            console.log(
              "[CGM] bind success — warmupEnd:",
              warmupEnd,
              "| raw initEndDate:",
              d.initEndDate,
              "| raw bindDate:",
              d.bindDate
            );
            if (warmupEnd) {
              dispatch(
                bindSuccess({
                  pocDevice: d.pocDevice,
                  initEndDate: warmupEnd,
                })
              );
              storage.setItem(STORAGE_KEYS.CGM_WARMUP_END_DATE, warmupEnd);
            } else {
              dispatch(connected(d.pocDevice));
            }
            dispatch(setStep("place-sensor"));
          }
          break;

        case POST_MESSAGE_EVENTS.CGM_BIND_FAILED:
          dispatch(bindFailed(String(data?.error ?? "Binding failed")));
          break;

        case POST_MESSAGE_EVENTS.CGM_CONNECTED:
          if (data && (data as { pocDevice?: PocDeviceJson }).pocDevice) {
            const restoreStep = cgm.step;
            dispatch(
              connected((data as { pocDevice: PocDeviceJson }).pocDevice)
            );
            if (
              restoreStep === "device-ready" ||
              restoreStep === "place-sensor" ||
              restoreStep === "place-transmitter"
            ) {
              dispatch(setStep(restoreStep));
            }
          }
          break;

        case POST_MESSAGE_EVENTS.CGM_DISCONNECTED:
          dispatch(disconnected());
          break;

        case POST_MESSAGE_EVENTS.CGM_WARMUP_STARTED: {
          const restoreStep = cgm.step;
          const wEnd = resolveWarmupEnd(
            data?.initEndDate as string | null | undefined,
            data?.bindDate as string | null | undefined
          );
          console.log("[CGM] warmup started — warmupEnd:", wEnd);
          if (wEnd) {
            dispatch(warmupStarted(wEnd));
            storage.setItem(STORAGE_KEYS.CGM_WARMUP_END_DATE, wEnd);
          } else if (!isCgmConnectionJourneyStep(cgm.step)) {
            dispatch(setStep("connected"));
          }
          if (
            restoreStep === "device-ready" ||
            restoreStep === "place-sensor" ||
            restoreStep === "place-transmitter"
          ) {
            dispatch(setStep(restoreStep));
          }
          break;
        }

        case POST_MESSAGE_EVENTS.CGM_GLUCOSE_DATA:
          if (data?.readings) {
            const readings = data.readings as PocGlucoseJson[];
            console.log(
              "[CGM] glucose data:",
              readings.length,
              "reading(s)",
              readings.length > 0
                ? {
                    latestMg:
                      readings[readings.length - 1]?.showGlucoseMG,
                  }
                : "",
              data.batteryPercent != null
                ? `| battery: ${data.batteryPercent}%`
                : ""
            );
            dispatch(
              glucoseDataReceived({
                readings,
                batteryPercent: data.batteryPercent as
                  | number
                  | null
                  | undefined,
              })
            );
          }
          break;

        case POST_MESSAGE_EVENTS.CGM_SENSOR_STATE:
          if (data?.sensorState != null) {
            const restoreStep = cgm.step;
            console.log(
              "[CGM] sensor state:",
              data.sensorState,
              data.statusName ?? "",
              "| current step:",
              cgm.step,
              "| warmupEndDate:",
              cgm.warmupEndDate
            );
            dispatch(
              sensorStateChanged({
                sensorState: data.sensorState as number,
                statusName: data.statusName as string | undefined,
              })
            );
            if (
              restoreStep === "device-ready" ||
              restoreStep === "place-sensor" ||
              restoreStep === "place-transmitter"
            ) {
              dispatch(setStep(restoreStep));
            }
          }
          break;

        case POST_MESSAGE_EVENTS.CGM_STATUS_UPDATE:
          if (data) {
            const restoreStep = cgm.step;
            let finalStatus = data.status as string;
            let resolvedInitEnd = data.initEndDate as string | null | undefined;
            if (finalStatus === "warmup") {
              const warmupEnd = resolveWarmupEnd(
                data.initEndDate as string | null | undefined,
                data.bindDate as string | null | undefined
              );
              console.log(
                "[CGM] status warmup — warmupEnd:",
                warmupEnd,
                "| raw initEndDate:",
                data.initEndDate,
                "| raw bindDate:",
                data.bindDate
              );
              if (warmupEnd) {
                resolvedInitEnd = warmupEnd;
                storage.setItem(STORAGE_KEYS.CGM_WARMUP_END_DATE, warmupEnd);
              } else {
                finalStatus = "bound";
                console.log(
                  "[CGM] status warmup → warmup is over, treating as bound"
                );
              }
            }
            dispatch(
              statusUpdate({
                status: finalStatus,
                pocDevice: data.pocDevice as PocDeviceJson | null | undefined,
                initEndDate: resolvedInitEnd,
              })
            );
            if (
              restoreStep === "device-ready" ||
              restoreStep === "place-sensor" ||
              restoreStep === "place-transmitter"
            ) {
              dispatch(setStep(restoreStep));
            }
          }
          break;

        case POST_MESSAGE_EVENTS.CGM_ERROR: {
          const msg = String(data?.error ?? "Unknown error");
          dispatch(cgmError(msg));
          const isFreshConnection = cgm.step === "intro" && !cgm.boundDevice;
          const isNoBoundDeviceError =
            /no bound device|bound device to reconnect/i.test(msg);
          if (!(isFreshConnection && isNoBoundDeviceError)) {
            toast.error(msg);
          }
          break;
        }

        case POST_MESSAGE_EVENTS.CGM_QR_CANCELLED:
          console.log("[CGM] QR cancelled — returning to scanning");
          dispatch(setStep("scanning"));
          dispatch(clearScannedDevices());
          sendMessageToParent(POST_MESSAGE_EVENTS.START_CGM_PAIRING, {});
          break;
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [
    dispatch,
    cgm.step,
    cgm.boundDevice,
    cgm.warmupEndDate,
    resolveWarmupEnd,
  ]);
}
